import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';

/** POST /api/Menu/Save -> MenuRequest */
export interface MenuRequest {
  menuId: number;
  parentId?: number | null;
  title: string;
  route?: string | null;
  icon?: string | null;
  displayOrder: number;
  isActive: boolean;
  /** Not part of the menu-based spec; kept optional so legacy screens keep compiling. */
  permissionCode?: string | null;
}

/** A node as returned by GetTree / GetMyMenu (parent menus with nested children). */
export interface MenuNode extends MenuRequest {
  children?: MenuNode[];
}

/** GET /api/Menu/GetUserOverrides -> per-user grant/deny rows. */
export type OverrideSource = 'GRANT' | 'DENY';
export interface UserOverride {
  menuId: number;
  title?: string | null;
  route?: string | null;
  source: OverrideSource;
}

/** POST /api/Menu/AssignToRole */
export interface RoleMenuRequest {
  roleId: number;
  menuIds: number[];
}

/** POST /api/Menu/AssignToUser */
export interface UserMenuRequest {
  userId: number;
  grantMenuIds: number[];
  denyMenuIds: number[];
}

@Injectable({ providedIn: 'root' })
export class MenuService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/Menu`;

  /** Shared state: the logged-in user's effective menu (sidebar) and the flat set of routes
   *  they may open (consumed by authGuard). Loaded once via loadMyMenu(). */
  readonly myMenu = signal<MenuNode[]>([]);
  readonly allowedRoutes = signal<Set<string>>(new Set());
  readonly loaded = signal(false);

  // ===================== queries =====================
  getAll() {
    return this.http.get(`${this.baseUrl}/GetAll`);
  }

  getTree() {
    return this.http.get(`${this.baseUrl}/GetTree`);
  }

  getById(id: number) {
    return this.http.get(`${this.baseUrl}/GetById`, { params: { id } });
  }

  /** Effective menu tree for an arbitrary user (admin preview of the assignment outcome). */
  getByUserId(userId: number) {
    return this.http.get(`${this.baseUrl}/GetByUserId`, { params: { userId } });
  }

  /** Raw GET /api/Menu/GetMyMenu. */
  getMyMenu() {
    return this.http.get(`${this.baseUrl}/GetMyMenu`);
  }

  /** Load GetMyMenu into shared state; returns the parsed tree. */
  loadMyMenu(): Observable<MenuNode[]> {
    return this.getMyMenu().pipe(
      map((res: any) => {
        const data = res?.data ?? res ?? [];
        return Array.isArray(data) ? (data as MenuNode[]) : [];
      }),
      tap((tree) => {
        this.myMenu.set(tree);
        this.allowedRoutes.set(this.collectRoutes(tree));
        this.loaded.set(true);
      }),
    );
  }

  private collectRoutes(tree: MenuNode[]): Set<string> {
    const set = new Set<string>();
    const walk = (nodes: MenuNode[]) =>
      nodes.forEach((n) => {
        if (n.route) set.add(n.route);
        if (n.children?.length) walk(n.children);
      });
    walk(tree);
    return set;
  }

  isRouteAllowed(route: string): boolean {
    return this.allowedRoutes().has(route);
  }

  /** The user's first accessible route (used as a landing fallback), or null if they have none. */
  firstAllowedRoute(): string | null {
    const next = this.allowedRoutes().values().next();
    return next.done ? null : next.value;
  }

  /** Clear cached menu state — call on logout so the next user loads their own menu. */
  reset() {
    this.myMenu.set([]);
    this.allowedRoutes.set(new Set());
    this.loaded.set(false);
  }

  /** Server-side hard check (GET /api/Menu/CanAccess?route=). */
  canAccess(route: string) {
    return this.http.get(`${this.baseUrl}/CanAccess`, { params: { route } });
  }

  // ===================== CRUD (Menu Master) =====================
  save(menu: MenuRequest) {
    return this.http.post(`${this.baseUrl}/Save`, menu);
  }

  delete(id: number) {
    return this.http.get(`${this.baseUrl}/Delete`, { params: { id } });
  }

  // ===================== role / user assignment =====================
  getRoleMenuIds(roleId: number) {
    return this.http.get(`${this.baseUrl}/GetRoleMenuIds`, { params: { roleId } });
  }

  assignToRole(body: RoleMenuRequest) {
    return this.http.post(`${this.baseUrl}/AssignToRole`, body);
  }

  getUserOverrides(userId: number) {
    return this.http.get(`${this.baseUrl}/GetUserOverrides`, { params: { userId } });
  }

  assignToUser(body: UserMenuRequest) {
    return this.http.post(`${this.baseUrl}/AssignToUser`, body);
  }
}
